import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Linkedin, Github, Loader2 } from "lucide-react";
import axios from "axios";
import { PROFILE } from "@/data/content";
import SignOff from "@/components/SignOff";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Finale() {
  const ref = useRef(null);
  const formRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — I'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Try emailing me directly.");
    } finally {
      setSending(false);
    }
  };

  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="finale-section"
      className="relative z-30 overflow-hidden bg-[#050505]"
    >
      {/* Parallax ambient glow */}
      <motion.div
        style={{ y: bgY }}
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
      >
        <div className="absolute right-[8%] top-[20%] h-[60vmin] w-[60vmin] rounded-full bg-[#10b981]/12 blur-[140px]" />
        <div className="absolute left-[2%] bottom-[6%] h-[40vmin] w-[40vmin] rounded-full bg-[#10b981]/8 blur-[120px]" />
      </motion.div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/40" />
      <div className="pointer-events-none absolute left-1/4 top-1/3 h-[50vmin] w-[50vmin] rounded-full bg-[#10b981]/15 blur-[130px]" />

      <div className="relative mx-auto max-w-[1500px] px-6 py-28 md:px-10 md:py-40">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mb-6 flex items-center gap-4 text-xs uppercase tracking-[0.35em] text-[#7c7a72]">
            <span className="h-px w-10 bg-[#10b981]" />
            The next chapter
          </div>
          <h2 className="font-display text-[16vw] leading-[0.82] text-[#f2f0e6] md:text-[11vw]">
            Let's build
            <br />
            <span className="text-stroke-emerald">something.</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-14 md:grid-cols-2">
          {/* CTAs + socials */}
          <div>
            <p className="max-w-md text-base md:text-lg leading-relaxed text-[#b9b6a8]">
              Got an idea worth shipping, an internship, or just want to talk AI
              and building? I'm always up for a good conversation.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => toast("Resume coming soon — ping me and I'll send it over.")}
                data-testid="view-resume-btn"
                className="group inline-flex min-h-[44px] items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-[#f2f0e6] transition-colors duration-300 hover:border-[#f2f0e6] hover:bg-[#f2f0e6] hover:text-[#050505]"
              >
                View Resume
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </button>
              <button
                type="button"
                onClick={scrollToForm}
                data-testid="get-in-touch-btn"
                className="group inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[#10b981] px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-[#04140d] transition-colors duration-300 hover:bg-[#0ea371]"
              >
                Get in Touch
                <Mail className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-12 flex flex-col gap-3">
              <a
                href={`mailto:${PROFILE.email}`}
                data-testid="footer-email"
                className="group inline-flex items-center gap-3 text-[#b9b6a8] transition-colors duration-300 hover:text-[#10b981]"
              >
                <Mail className="h-5 w-5" /> {PROFILE.email}
              </a>
              <a
                href={PROFILE.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="group inline-flex items-center gap-3 text-[#b9b6a8] transition-colors duration-300 hover:text-[#10b981]"
              >
                <Linkedin className="h-5 w-5" /> LinkedIn — /piyush-soni01
              </a>
              <a
                href={PROFILE.github}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-github"
                className="group inline-flex items-center gap-3 text-[#b9b6a8] transition-colors duration-300 hover:text-[#10b981]"
              >
                <Github className="h-5 w-5" /> GitHub
              </a>
            </div>
          </div>

          {/* Contact form */}
          <form
            ref={formRef}
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 md:p-9 backdrop-blur-sm"
          >
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#7c7a72]">
              Name
            </label>
            <input
              name="name"
              value={form.name}
              onChange={change}
              data-testid="contact-name"
              placeholder="Your name"
              className="mb-6 w-full rounded-lg border border-white/10 bg-[#050505] px-4 py-3 text-[#f2f0e6] outline-none transition-colors duration-300 placeholder:text-[#5a584f] focus:border-[#10b981]"
            />
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#7c7a72]">
              Email
            </label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={change}
              data-testid="contact-email"
              placeholder="you@example.com"
              className="mb-6 w-full rounded-lg border border-white/10 bg-[#050505] px-4 py-3 text-[#f2f0e6] outline-none transition-colors duration-300 placeholder:text-[#5a584f] focus:border-[#10b981]"
            />
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#7c7a72]">
              Message
            </label>
            <textarea
              name="message"
              value={form.message}
              onChange={change}
              data-testid="contact-message"
              rows={4}
              placeholder="What are we building?"
              className="mb-6 w-full resize-none rounded-lg border border-white/10 bg-[#050505] px-4 py-3 text-[#f2f0e6] outline-none transition-colors duration-300 placeholder:text-[#5a584f] focus:border-[#10b981]"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#10b981] px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-[#04140d] transition-colors duration-300 hover:bg-[#0ea371] disabled:opacity-60"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending
                </>
              ) : (
                <>Send message</>
              )}
            </button>
          </form>
        </div>

        <SignOff />

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-sm text-[#7c7a72] md:mt-24 md:flex-row md:items-center">
          <span className="font-display text-2xl tracking-wide text-[#f2f0e6]">
            PIYUSH SONI<span className="text-[#10b981]">.</span>
          </span>
          <span>© {new Date().getFullYear()} Piyush — Built with intent.</span>
        </div>
      </div>
    </section>
  );
}
